import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, FileText, Check, ShieldCheck, Loader2, Receipt, CreditCard } from "lucide-react";

export default function Billing() {
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedPropId, setSelectedPropId] = useState("");
  const [processing, setProcessing] = useState(null);

  const reload = async () => {
    const [s, p, pay, props] = await Promise.all([
      api.get("/billing/status"),
      api.get("/billing/products"),
      api.get("/billing/payments"),
      api.get("/properties"),
    ]);
    setStatus(s.data);
    setProducts(p.data);
    setPayments(pay.data);
    setProperties(props.data);
    if (!selectedPropId && props.data[0]) setSelectedPropId(props.data[0].id);
  };

  useEffect(() => { reload(); }, []);

  if (!status) return <div className="p-10 text-slate-500">Loading billing…</div>;

  const hasPayPal = Boolean(status.paypal_client_id);

  const proProduct = products.find((p) => p.key === "pro_monthly");
  const reportProduct = products.find((p) => p.key === "report_property");

  const sdkOptions = {
    "clientId": status.paypal_client_id,
    currency: "USD",
    intent: "capture",
    components: "buttons",
    disableFunding: "card,credit",
  };

  const page = (
      <div className="p-6 md:p-10 space-y-8 max-w-6xl" data-testid="billing-page">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Billing</div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900">Upgrade & receipts</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">Unlock Pro across your whole portfolio, or buy a one-off compliance report for a specific property. Pay by card with Stripe or with PayPal — both in test mode.</p>
        </div>

        {/* Current Pro banner */}
        <div className={`rounded-2xl border p-5 flex items-center gap-4 ${status.pro_active ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`} data-testid="pro-status">
          <ShieldCheck className={`w-8 h-8 ${status.pro_active ? "text-emerald-600" : "text-slate-400"}`} />
          <div className="flex-1">
            <div className="font-display font-semibold text-slate-900">
              {status.pro_active ? "HavenGuard Pro is active" : "You're on the free plan"}
            </div>
            <div className="text-sm text-slate-600 mt-0.5">
              {status.pro_active
                ? <>Renews / expires on <b>{fmtDate(status.pro_until)}</b>. Reports purchased: {status.reports_purchased}.</>
                : "Upgrade for unlimited properties, AI Assistant, and audit-ready reports."}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Pro Monthly */}
          <ProductCard
            testid="product-pro"
            icon={Sparkles}
            title={proProduct?.name || "Pro"}
            price={proProduct?.price}
            description={proProduct?.description}
            features={["Unlimited properties", "AI regulatory Q&A", "Renewal reminders", "Priority updates"]}
            processing={processing === "pro_monthly"}
          >
            <StripeButton testid="stripe-pay-pro-btn" product="pro_monthly"
              onStart={() => setProcessing("pro_monthly")} onFail={() => setProcessing(null)} />
            {hasPayPal && <OrDivider />}
            {hasPayPal && <PayPalButtons
              style={{ layout: "vertical", shape: "pill", label: "pay" }}
              createOrder={async () => {
                setProcessing("pro_monthly");
                try {
                  const { data } = await api.post("/billing/orders", { product: "pro_monthly" });
                  return data.orderID;
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Could not start payment");
                  setProcessing(null);
                  throw err;
                }
              }}
              onApprove={async (data) => {
                try {
                  const res = await api.post(`/billing/orders/${data.orderID}/capture`);
                  if (res.data.status === "COMPLETED") {
                    toast.success("Pro activated for 30 days");
                    await reload();
                  } else {
                    toast.warning(`Payment status: ${res.data.status}`);
                  }
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Capture failed");
                } finally { setProcessing(null); }
              }}
              onCancel={() => { setProcessing(null); toast("Payment cancelled"); }}
              onError={() => { setProcessing(null); toast.error("PayPal error — please try again"); }}
            />}
          </ProductCard>

          {/* Per-property Report */}
          <ProductCard
            testid="product-report"
            icon={FileText}
            title={reportProduct?.name || "Compliance Report"}
            price={reportProduct?.price}
            description={reportProduct?.description}
            features={["PDF export", "Includes all rules + status", "Property permits & deadlines", "Auditor / insurer ready"]}
            processing={processing === "report_property"}
          >
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500 block mb-1">Property</label>
            <select
              data-testid="report-property-select"
              value={selectedPropId}
              onChange={(e) => setSelectedPropId(e.target.value)}
              className="w-full mb-3 px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm">
              {properties.length === 0 && <option value="">Add a property first</option>}
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}{p.has_report ? " (already purchased)" : ""}</option>)}
            </select>
            <StripeButton testid="stripe-pay-report-btn" product="report_property" propertyId={selectedPropId}
              disabled={!selectedPropId}
              onStart={() => setProcessing("report_property")} onFail={() => setProcessing(null)} />
            {hasPayPal && <OrDivider />}
            {hasPayPal && <PayPalButtons
              disabled={!selectedPropId}
              style={{ layout: "vertical", shape: "pill", label: "buynow" }}
              createOrder={async () => {
                setProcessing("report_property");
                try {
                  const { data } = await api.post("/billing/orders", { product: "report_property", property_id: selectedPropId });
                  return data.orderID;
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Could not start payment");
                  setProcessing(null);
                  throw err;
                }
              }}
              onApprove={async (data) => {
                try {
                  const res = await api.post(`/billing/orders/${data.orderID}/capture`);
                  if (res.data.status === "COMPLETED") {
                    toast.success("Report unlocked. We've emailed you a download link.");
                    await reload();
                  } else { toast.warning(`Payment status: ${res.data.status}`); }
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Capture failed");
                } finally { setProcessing(null); }
              }}
              onCancel={() => { setProcessing(null); toast("Payment cancelled"); }}
              onError={() => { setProcessing(null); toast.error("PayPal error — please try again"); }}
            />}
          </ProductCard>
        </div>

        {/* Receipts */}
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="receipts">
          <div className="flex items-center gap-2 mb-4">
            <Receipt className="w-4 h-4 text-slate-500" />
            <h2 className="font-display font-semibold text-slate-900">Payment history</h2>
          </div>
          {payments.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No payments yet. Your receipts will appear here.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {payments.map((p) => (
                <div key={p.id} data-testid={`receipt-${p.id}`} className="py-3 flex items-center gap-4 flex-wrap">
                  <div className={`w-2 h-2 rounded-full ${p.status === "COMPLETED" ? "bg-emerald-500" : ["CREATED", "PENDING"].includes(p.status) ? "bg-amber-400" : "bg-slate-300"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 text-sm">{PRODUCT_LABELS[p.product] || p.product}</div>
                    <div className="text-xs text-slate-500 font-mono">
                      <span data-testid={`receipt-provider-${p.id}`}>{p.provider === "stripe" ? "Card · Stripe" : "PayPal"}</span> · {p.id.slice(0, 12)}… · {fmtDate(p.created_at)}
                    </div>
                  </div>
                  <div className="text-sm font-mono text-slate-900">${p.amount}</div>
                  <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full ${p.status === "COMPLETED" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500"}`}>{p.status}</span>
                  {p.provider !== "stripe" && status.paypal_env === "sandbox" && p.status !== "COMPLETED" && (
                    <button
                      data-testid={`simulate-${p.id}`}
                      onClick={async () => {
                        try {
                          const res = await api.post(`/billing/orders/${p.id}/simulate-approval`);
                          if (res.data.status === "COMPLETED") {
                            toast.success(p.product === "pro_monthly" ? "Pro activated for 30 days (simulated)" : "Report unlocked (simulated)");
                            await reload();
                          }
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Simulation failed");
                        }
                      }}
                      className="text-[11px] font-medium px-2.5 py-1 rounded-lg border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 transition">
                      Simulate approval
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
  );

  return hasPayPal ? <PayPalScriptProvider options={sdkOptions}>{page}</PayPalScriptProvider> : page;
}

function StripeButton({ product, propertyId, disabled, onStart, onFail, testid }) {
  const startCheckout = async () => {
    onStart();
    try {
      const { data } = await api.post("/payments/checkout", {
        product, property_id: propertyId || null, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start card checkout");
      onFail();
    }
  };
  return (
    <button type="button" data-testid={testid} disabled={disabled} onClick={startCheckout}
      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white py-3 text-sm font-medium hover:bg-slate-800 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition-[background-color,transform]">
      <CreditCard className="w-4 h-4" /> Pay with card
    </button>
  );
}

function OrDivider() {
  return (
    <div className="flex items-center gap-3 my-3 text-[10px] font-mono uppercase tracking-widest text-slate-400">
      <span className="h-px flex-1 bg-slate-200" /> or PayPal <span className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

const PRODUCT_LABELS = { pro_monthly: "HavenGuard Pro (30 days)", report_property: "Compliance Report" };

function ProductCard({ icon: Icon, title, price, description, features, children, processing, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 relative overflow-hidden" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <Icon className="w-6 h-6 text-slate-700" />
          <h3 className="mt-3 text-xl font-display font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-600 mt-1 max-w-sm">{description}</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold font-display text-slate-900">${price}</div>
          <div className="text-[10px] font-mono uppercase text-slate-500">USD</div>
        </div>
      </div>
      <ul className="mt-4 space-y-1.5">
        {features.map((f) => (
          <li key={f} className="text-sm text-slate-700 flex items-center gap-2">
            <Check className="w-3.5 h-3.5 text-emerald-500" /> {f}
          </li>
        ))}
      </ul>
      <div className="mt-5">{children}</div>
      {processing && (
        <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center">
          <div className="inline-flex items-center gap-2 text-slate-700 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Processing…</div>
        </div>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}
